import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  CreateRouterConnectionDto,
  RouterConnectionType,
  RouterIntegrationType,
} from '../../types/router-connection/router-connection.type';
import { setShowModal, setModalContent } from '../../features/utils/utils.slice';
import { PaginationType } from '../../types/utils/pagination.type';
import { SyncRouterType } from '../../types/router/sync-router/sync-router.type';
import { getRouterSyncAsyncTaskList, getRouterUpdateAsyncTaskList } from '../logs/logs.action';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import {
  CreateMicroserviceRouterDto,
  InterfaceRouterStatus,
  MicroserviceRouterType,
  UpdateMicroserviceRouterDto,
} from '../../types/router/router.type';
import { AsyncTaskType } from '../../types/async-task/async-task.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import {
  RouterAdminCredentialHistoryType,
  RouterAdminCredentialsType,
} from '../../types/router/router-admin-credentials.type';
import { ROUTER_ADMIN_CREDENTIAL_HISTORY_LIMIT } from '../../utils/router-admin-credentials.utils';

export const postRouterConnection = createAsyncThunk<
  RouterConnectionType,
  { data: CreateRouterConnectionDto },
  { state: RootReducerState }
>('routers/postRouterConnection', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<RouterConnectionType>(
      `${process.env.REACT_APP_API_URL}/routers/router-connections`,
      data
    );

    thunkAPI.dispatch(getRouterConnectionList({ organizationId: data.organizationId }));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating connection!' });
  }
});

export const getRouterConnectionList = createAsyncThunk<
  Array<RouterConnectionType>,
  { organizationId: number },
  { state: RootReducerState }
>('routers/getRouterConnectionList', async (data, thunkAPI) => {
  try {
    const response = await axios.get<{ items: Array<RouterConnectionType> }>(
      `${process.env.REACT_APP_API_URL}/routers/router-connections?organizationId=${data.organizationId}`
    );
    return response.data?.items || [];
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching connections!' });
  }
});

export const deleteRouterConnection = createAsyncThunk<
  { success: boolean },
  { organizationId: number; id: number },
  { state: RootReducerState }
>('routers/deleteRouterConnection', async (data, thunkAPI) => {
  try {
    const response = await axios.delete<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/routers/router-connections/${data.id}`
    );

    thunkAPI.dispatch(getRouterConnectionList({ organizationId: data.organizationId }));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting organization connection!' });
  }
});

export const getRouterIntegrationsList = createAsyncThunk<
  Array<RouterIntegrationType>,
  void,
  { state: RootReducerState }
>('routers/getRouterIntegrationsList', async (_, thunkAPI) => {
  try {
    const response = await axios.get<{ items: Array<RouterIntegrationType> }>(
      `${process.env.REACT_APP_API_URL}/routers/router-integrations`
    );
    return response.data?.items || [];
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching integrations!' });
  }
});

export const postSyncExternalRouters = createAsyncThunk<
  { success: boolean },
  { connectionId: number; imeis?: Array<string> },
  { state: RootReducerState }
>('routers/postSyncExternalRouters', async (data, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/routers/router-sync/sync-external-routers`,
      data
    );

    thunkAPI.dispatch(
      getRouterSyncAsyncTaskList({
        type: 'Connection',
        connectionID: data.connectionId,
      })
    );

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting external routers list!' });
  }
});

export const postSyncRouters = createAsyncThunk<
  { success: boolean },
  {
    add: Array<number>;
    remove: Array<number>;
    connectionId: number;
    syncedEquipmentGroupId?: number;
    syncedWarehouseId?: number;
  },
  { state: RootReducerState }
>('routers/postSyncRouters', async (data, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/routers/router-sync/sync-routers`,
      data
    );

    thunkAPI.dispatch(getSyncRouters(thunkAPI.getState().routersReducer.syncRouterListParams));
    thunkAPI.dispatch(getSyncRouters(thunkAPI.getState().routersReducer.noSyncRouterListParams));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(setShowModal(false));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during syncing routers list!' });
  }
});

export const patchSyncRouters = createAsyncThunk<
  { success: boolean },
  { connectionId: number },
  { state: RootReducerState }
>('routers/patchSyncRouters', async (data, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/routers/router-sync/sync-routers`,
      data
    );

    thunkAPI.dispatch(getRouterUpdateAsyncTaskList({ type: 'Connection', connectionID: data.connectionId }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating our routers list!' });
  }
});

export const getSyncRouters = createAsyncThunk<
  PaginationType<SyncRouterType>,
  { connectionId: number; limit: number; page: number; q: string; isSynced: boolean },
  { state: RootReducerState }
>('routers/getSyncRouters', async (data, thunkAPI) => {
  try {
    const response = await axios.get<PaginationType<SyncRouterType>>(
      `${process.env.REACT_APP_API_URL}/routers/router-sync?connectionId=${data.connectionId}&limit=${data.limit}&page=${data.page}&q=${data.q}&isSynced=${data.isSynced}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting routers list!' });
  }
});

export const deleteSyncRouter = createAsyncThunk<{ success: boolean }, { id: number }, { state: RootReducerState }>(
  'routers/deleteSyncRouter',
  async (data, thunkAPI) => {
    try {
      const response = await axios.delete<{ success: boolean }>(
        `${process.env.REACT_APP_API_URL}/routers/router-sync/${data.id}`
      );

      thunkAPI.dispatch(getSyncRouters(thunkAPI.getState().routersReducer.syncRouterListParams));
      thunkAPI.dispatch(getSyncRouters(thunkAPI.getState().routersReducer.noSyncRouterListParams));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting sync router!' });
    }
  }
);

export const getRoutersList = createAsyncThunk<
  PaginationType<MicroserviceRouterType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('routers/getRoutersList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<MicroserviceRouterType>>(
      `${process.env.REACT_APP_API_URL}/routers${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data || [];
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching routers!' });
  }
});

export const postRouterDetails = createAsyncThunk<
  MicroserviceRouterType,
  { data: CreateMicroserviceRouterDto },
  { state: RootReducerState }
>('routers/postRouterDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<MicroserviceRouterType>(`${process.env.REACT_APP_API_URL}/routers/`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating router!' });
  }
});

export const postRoutersDetails = createAsyncThunk<
  AsyncTaskType,
  {
    data: {
      owner?: { id: number };
      equipment_group: { id: number };
      pending_type: string;
      routers: Array<
        Pick<MicroserviceRouterType, 'serial_number' | 'imei' | 'product_name' | 'pending_type'> & {
          is_inventory: boolean;
        }
      >;
    };
  },
  { state: RootReducerState }
>('routers/postRoutersDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<AsyncTaskType>(`${process.env.REACT_APP_API_URL}/routers/bulk`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating routers!' });
  }
});

export const getRouterDetails = createAsyncThunk<MicroserviceRouterType, { id: number }, { state: RootReducerState }>(
  'routers/getRouterDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<MicroserviceRouterType>(`${process.env.REACT_APP_API_URL}/routers/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching router!' });
    }
  }
);

export const patchRouterDetails = createAsyncThunk<
  MicroserviceRouterType,
  { id: number; data: UpdateMicroserviceRouterDto },
  { state: RootReducerState }
>('routers/patchRouterDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<MicroserviceRouterType>(`${process.env.REACT_APP_API_URL}/routers/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating router!' });
  }
});

export const deleteRouterDetails = createAsyncThunk<
  { success: true },
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('routers/deleteRouterDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<{ success: true }>(`${process.env.REACT_APP_API_URL}/routers/${id}`);
    if (fetchList) thunkAPI.dispatch(getRoutersList(thunkAPI.getState().routersReducer.routersRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting router!' });
  }
});

export const updateRouterCurrentStatus = createAsyncThunk<
  MicroserviceRouterType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('routers/updateRouterCurrentStatus', async ({ id, fetchList }, thunkAPI) => {
  try {
    if (fetchList) thunkAPI.dispatch(getRoutersList(thunkAPI.getState().routersReducer.routersRouteParams));

    const response = await axios.patch<MicroserviceRouterType>(
      `${process.env.REACT_APP_API_URL}/routers/${id}/update-current-status`,
      null
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating router current status!' });
  }
});

export const refreshRouterSignal = createAsyncThunk<
  MicroserviceRouterType,
  { id: number },
  { state: RootReducerState }
>('routers/refreshRouterSignal', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.post<MicroserviceRouterType>(
      `${process.env.REACT_APP_API_URL}/routers/${id}/refresh-signal`,
      null
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during refreshing router signal!' });
  }
});

export const getRouterInterfaceStats = createAsyncThunk<
  InterfaceRouterStatus,
  { id: number; end_date?: string; start_date?: string; range?: number; type: string },
  { state: RootReducerState }
>('routers/getRouterInterfaceStats', async ({ id, end_date, range, type, start_date }, thunkAPI) => {
  try {
    const params = { end_date, start_date, range, type };
    const queryString = Object.keys(params)
      .map(key => {
        if (params[key]) return key + '=' + params[key];
        return key + '=';
      })
      .join('&');

    const response = await axios.get<InterfaceRouterStatus>(
      `${process.env.REACT_APP_API_URL}/routers/interface/${id}/stats${queryString ? `?${queryString}` : ''}`
    );

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching interface stats!' });
  }
});

export const rebootRouter = createAsyncThunk<
  { success: boolean; message?: string },
  { id: number },
  { state: RootReducerState }
>('routers/rebootRouter', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean; message?: string }>(
      `${process.env.REACT_APP_API_URL}/routers/${id}/reboot`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during rebooting router!' });
  }
});

export const resyncRouter = createAsyncThunk<{ success: boolean }, { id: number }, { state: RootReducerState }>(
  'routers/resyncRouter',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.post<{ success: boolean }>(`${process.env.REACT_APP_API_URL}/routers/${id}/resync`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during re-syncing router!' });
    }
  }
);

export const getRouterLoginUrl = createAsyncThunk<
  { success: boolean; loginUrl?: string; expiresAt?: string },
  { id: number },
  { state: RootReducerState }
>('routers/getRouterLoginUrl', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean; loginUrl?: string; expiresAt?: string }>(
      `${process.env.REACT_APP_API_URL}/routers/${id}/login-url`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting router login URL!' });
  }
});

export const getRouterAdminCredentials = createAsyncThunk<
  RouterAdminCredentialsType,
  { id: number },
  { state: RootReducerState }
>('routers/getRouterAdminCredentials', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<RouterAdminCredentialsType>(
      `${process.env.REACT_APP_API_URL}/routers/${id}/admin-credentials`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching router admin credentials!' });
  }
});

export const getRouterAdminCredentialHistory = createAsyncThunk<
  RouterAdminCredentialHistoryType,
  { id: number; page?: number; limit?: number },
  { state: RootReducerState }
>(
  'routers/getRouterAdminCredentialHistory',
  async ({ id, page = 1, limit = ROUTER_ADMIN_CREDENTIAL_HISTORY_LIMIT }, thunkAPI) => {
    try {
      const response = await axios.get<RouterAdminCredentialHistoryType>(
        `${process.env.REACT_APP_API_URL}/routers/${id}/admin-credentials/history?page=${page}&limit=${limit}`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({
        thunkAPI,
        error,
        fallback: 'Error during fetching router admin credential history!',
      });
    }
  }
);

export const saveRouterPortalCredentials = createAsyncThunk<
  { success: boolean },
  { id: number; username: string; password: string },
  { state: RootReducerState }
>('routers/saveRouterPortalCredentials', async ({ id, username, password }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/routers/${id}/portal-credentials`,
      { username, password }
    );

    thunkAPI.dispatch(getRouterDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during saving router portal credentials!' });
  }
});

export const updateRouterAdminPassword = createAsyncThunk<
  { success: boolean; password?: string; username?: string; message?: string; jobId?: string; status?: string },
  { id: number; pushToDevice?: boolean; syncFromProvider?: boolean },
  { state: RootReducerState }
>('routers/updateRouterAdminPassword', async ({ id, pushToDevice, syncFromProvider }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
      password?: string;
      username?: string;
      message?: string;
      jobId?: string;
      status?: string;
    }>(`${process.env.REACT_APP_API_URL}/routers/${id}/admin-password`, { pushToDevice, syncFromProvider });

    thunkAPI.dispatch(getRouterAdminCredentials({ id }));
    if (thunkAPI.getState().routersReducer.routerAdminCredentialHistory) {
      thunkAPI.dispatch(getRouterAdminCredentialHistory({ id }));
    }

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating router admin password!' });
  }
});

export const refreshRouterAdminPasswordUpdateStatus = createAsyncThunk<
  { success: boolean; status: string; message?: string },
  { id: number },
  { state: RootReducerState }
>('routers/refreshRouterAdminPasswordUpdateStatus', async ({ id }, thunkAPI) => {
  try {
    const credentials = await thunkAPI.dispatch(getRouterAdminCredentials({ id })).unwrap();
    if (thunkAPI.getState().routersReducer.routerAdminCredentialHistory) {
      thunkAPI.dispatch(getRouterAdminCredentialHistory({ id }));
    }

    return {
      success: true,
      status: credentials.status ?? '',
      message: credentials.message ?? undefined,
    };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during refreshing router admin password status!' });
  }
});

export const postRouterOwnershipTransfer = createAsyncThunk<
  void,
  {
    id: number;
    data: {
      owner: { id: number };
    };
  },
  { state: RootReducerState; rejectValue: string }
>('routers/postRouterOwnershipTransfer', async ({ id, data }, thunkAPI) => {
  try {
    if (!data.owner?.id) {
      return rejectWithApiError({ thunkAPI, error: null, fallback: 'Owner organization is required!' });
    }

    await axios.post(`${process.env.REACT_APP_API_URL}/routers/${id}/ownership-transfer`, data);
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during Router ownership transfer!' });
  }
});
